<template>
    <base-dialog :loading="loading" :value="dialog" :width="440" :title="$t('admin.invite_student')"
        @input="val => { if (!val) hide() }" @close="hide">
        <v-form v-model="valid" class="invite-body">
            <kimsa-combobox v-if="isCompanyAccount"
                :placeholder="`${$t('common.select_placeholder')} ${$t('common.coach')}`" dense :items="coaches"
                @change="coach = $event" :value="coach" item-value="id" :rules="coachRules" />

            <kimsa-toggle class="invite-toggle-row" :value="isMultiple" @change="onChangeMultiple">
                <span class="invite-toggle-label">{{ $t('common.invite_multiple_students') }}</span>
                <v-icon v-if="!can('bulkStudents')" size="14" class="ml-1">mdi-lock-outline</v-icon>
            </kimsa-toggle>

            <div v-if="isMultiple" class="form-group">
                <span class="field-label">{{ $t('common.file') }}</span>
                <kimsa-file-picker :value="file" accept=".xlsx, .xls, .csv" @change="file = $event" />
            </div>
            <div v-else class="form-group">
                <span class="field-label">{{ $t('coach.add_student_email') }}</span>
                <kimsa-text-field ref="email" :rules="emailRules" :placeholder="$t('entrance.email')"
                    icon="mdi-email-outline" input-mode="email" @change="email = $event" dense />
            </div>

            <v-alert v-if="errorMessage" type="error" text dense class="ma-0">
                {{ errorMessage }}
            </v-alert>
            <template v-if="needChangePlan">
                <v-alert v-if="!paymentsEnabled" color="accent" text dense class="ma-0">
                    {{ $t('payment.change_plan_from_desktop') }}
                </v-alert>
                <v-btn v-else @click="toChangePlan" color="primary" class="black--text" block depressed>
                    {{ $t('coach.update_plan') }}
                </v-btn>
            </template>
        </v-form>

        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.send')" :disabled="isMultiple ? !file : !valid"
                :loading="loading" @cancel="hide" @confirm="prepareInvitations" />
        </template>

        <select-plan-dialog ref="selectPlan" />
    </base-dialog>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import firebase, { db } from "@/utils/Firebase";
import { getCoachClientsLimit, skipsBilling } from "@/utils/Helpers";

import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import BaseDialog from "@/components/common/BaseDialog";
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
import KimsaFilePicker from "@/components/common/inputs/KimsaFilePicker";
import DialogActions from "@/components/common/DialogActions";
import SelectPlanDialog from "@/components/coach/SelectPlanDialog";

const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const normalizeEmail = value => String(value || '')
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/^\s*\d+\s*[.)-]\s*/, '')
    .trim()
    .toLowerCase()

export default {
    name: "InviteStudentDialog",
    components: { KimsaTextField, KimsaCombobox, BaseDialog, KimsaToggle, KimsaFilePicker, DialogActions, SelectPlanDialog },
    props: {
        coaches: {
            type: Array,
            default: () => [],
        }
    },
    data(vm) {
        return {
            dialog: false,
            valid: false,
            email: '',
            errorMessage: '',
            loading: false,
            needChangePlan: false,
            coachRules: [
                v => !!v || vm.$t('rules.required'),
            ],
            emailRules: [
                v => !!v || vm.$t('rules.required'),
                v => EMAIL_PATTERN.test(normalizeEmail(v)) || vm.$t('rules.invalid_email'),
            ],
            file: null,
            isMultiple: false,
            activeStudents: 0,
            maxClients: 0,
            coach: null,
        }
    },
    computed: {
        priceId(vm = this) {
            return vm.account?.stripeAccount?.currentSubscription?.priceId || null
        },
        coachId(vm = this) {
            return vm.coach?.id || vm.coach?.objectID || vm.currentUser.uid || null
        },
        coachEmail(vm = this) {
            return vm.coach?.email || vm.currentUser?.email || null
        },
        isCompanyAccount(vm = this) {
            return vm.accountType === 'company'
        }
    },
    mounted() {
        this.loadMaxClients()
    },
    methods: {
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.valid = false
            vm.email = ''
            if (vm.$refs.email) vm.$refs.email.reset()
            vm.errorMessage = ''
            vm.loading = false
            vm.needChangePlan = false
        },
        onChangeMultiple(val) {
            const vm = this
            if (val && !vm.requirePlan('bulkStudents')) return
            vm.isMultiple = val
        },
        inviteNewUser: firebase.functions().httpsCallable('httpInvitations-inviteStudent'),
        async loadMaxClients() {
            const vm = this;
            if (!skipsBilling(vm.account) && !vm.priceId && (vm.account?.accountType === 'coach' || vm.account?.accountType === 'company')) return vm.errorMessage = vm.$t('coach.subscription_problem')

            const { hasPlan, maxClients, activeStudents, canInvite, reason } = await getCoachClientsLimit(vm.coachId)
            if (reason === 'no-company') {
                vm.needChangePlan = false
                return vm.errorMessage = vm.$t('coach.not_linked_to_company')
            }
            if (!hasPlan) return

            vm.maxClients = maxClients
            vm.activeStudents = activeStudents
            if (!canInvite) {
                vm.needChangePlan = true
                vm.errorMessage = vm.$t('coach.limit_clients', { num: maxClients })
            } else {
                vm.needChangePlan = false
                vm.errorMessage = ''
            }
        },
        async prepareInvitations() {
            const vm = this
            await vm.withLoading(async () => {

            if (vm.isMultiple) {
                const buffer = await new Promise((resolve, reject) => {
                    const reader = new FileReader()
                    reader.onload = (e) => resolve(e.target.result)
                    reader.onerror = reject
                    reader.readAsArrayBuffer(vm.file)
                })

                const excel = new Uint8Array(buffer)
                const XLSX = (await import("xlsx")).default
                const workbook = XLSX.read(excel, { type: 'array' })
                const firstSheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[firstSheetName]
                const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

                let maxNewStudents = vm.maxClients - vm.activeStudents
                if (data.length - 1 > maxNewStudents) {
                    vm.errorMessage = vm.$t('coach.limit_clients', { num: vm.maxClients })
                    return
                }

                let sent = 0
                let invalid = 0
                for (let i = 0; i < data.length; i++) {
                    const row = (data[i] || []).map(normalizeEmail).filter(Boolean)
                    if (!row.length) continue
                    const email = row.find(value => EMAIL_PATTERN.test(value))
                    if (!email) {
                        invalid++
                        continue
                    }
                    if (await vm.confirmInvitation(email, true)) sent++
                }
                if (invalid > 0) vm.toast(vm.$t('coach.students_invitations_invalid', { n: invalid }), 'error')
                if (sent > 0) {
                    vm.toast(vm.$t('coach.students_invitations_sent', { n: sent }))
                    vm.hide()
                }
            } else {
                await vm.confirmInvitation(vm.email)
            }
            })
        },
        async confirmInvitation(rawEmail, isBulk = false) {
            const vm = this
            const currentEmail = normalizeEmail(rawEmail)
            if (!currentEmail) return false
            if (vm.coachEmail === currentEmail) {
                vm.errorMessage = vm.$t('coach.student_email_cant_be_same_than_yours')
                return false
            }

            const studentRef = await db.collection('users')
                .where('email', '==', currentEmail)
                .get()

            if (studentRef.docs.length > 0) {
                const existingStudent = studentRef.docs[0].data()
                if (existingStudent.accountType === 'coach') {
                    vm.errorMessage = vm.$t('coach.this_email_belongs_to_coach')
                    return false
                }
            }

            const payload = { studentEmail: currentEmail, coachId: vm.coachId }

            try {
                await vm.inviteNewUser(payload)
                if (!isBulk) {
                    vm.toast(vm.$t('coach.student_invitation_sent'))
                    vm.hide()
                }
                return true
            } catch (err) {
                vm.toast(`${err.message}. ${currentEmail}` || vm.$t('coach.student_invitation_failed'), 'error')
                return false
            }
        },
        toChangePlan() {
            const vm = this
            vm.$refs.selectPlan.show()
        },
    },
    watch: {

        priceId() {
            this.loadMaxClients()
        },
    }
}
</script>

<style scoped>
.invite-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.invite-toggle-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 0;
}

.invite-toggle-label {
    font-size: 14px;
    color: var(--v-txtPrimary-base);
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}
</style>